import React from 'react';
import { motion } from 'framer-motion';
import './Footer.css';

// Import SVG icons
import { ReactComponent as TwitterIcon } from '../../assets/icons/twitter.svg';
import { ReactComponent as DiscordIcon } from '../../assets/icons/discord.svg';
import { ReactComponent as TelegramIcon } from '../../assets/icons/medium.svg';
import { ReactComponent as GithubIcon } from '../../assets/icons/github.svg';

const Footer: React.FC = () => {
  const footerLinks = {
    product: [
      { name: 'Features', href: '#features' },
      { name: 'Tokenomics', href: '#tokenomics' },
      { name: 'Security', href: '/docs/security-audits' },
      { name: 'API Documentation', href: '/docs/api-docs' }
    ],
    company: [
      { name: 'About Us', href: '#team' },
      { name: 'What is E-Pay', href: '/docs/what-is-e-pay' },
      { name: 'User Guide', href: '/docs/user-guide' },
      { name: 'Scanner', href: '/scanner' }
    ],
    resources: [
      { name: 'Help Center', href: '/docs/faq' },
      { name: 'Technical Overview', href: '/docs/technical-overview' },
      { name: 'Whitepaper', href: '/docs/whitepaper' },
      { name: 'Audit Reports', href: '/docs/security-audits' }
    ],
    legal: [
      { name: 'Terms of Use', href: '/docs/terms-of-use' },
      { name: 'Smart Contracts', href: '/docs/smart-contract' },
      { name: 'Multi-Chain Networks', href: '/docs/multi-chain-networks' },
      { name: 'Token Features', href: '/docs/token-features-usdp' }
    ]
  };

  const socialLinks = [
    { name: 'Twitter', href: 'https://x.com/epayprotocol', icon: 'twitter' },
    { name: 'Discord', href: 'https://discord.gg/HKDRV5DQ', icon: 'discord' },
    { name: 'Telegram', href: '#', icon: 'telegram' },
    { name: 'GitHub', href: 'https://github.com/epayprotocol', icon: 'github' }
  ];

  return (
    <footer className="footer">
      {/* Main CTA Section */}
      <section className="footer-cta section-padding">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">
              Ready to Experience the Future of<br />
              <span className="title-gradient">Digital Payments?</span>
            </h2>
            <p className="cta-description">
              Join thousands of users who are already using USD.P for fast, secure, 
              and cost-effective digital transactions worldwide.
            </p>
            <div className="cta-actions">
              <motion.button
                className="btn btn-primary btn-large"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Get Started Today</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12H19M19 12L12 5M19 12L12 19"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.button>
              <motion.button
                className="btn btn-ghost btn-large"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Sales
              </motion.button>
            </div>
            <div className="cta-stats">
              <div className="cta-stat">
                <div className="stat-number">$12B+</div>
                <div className="stat-label">Total Volume</div>
              </div>
              <div className="cta-stat">
                <div className="stat-number">32K+</div>
                <div className="stat-label">Active Users</div>
              </div>
              <div className="cta-stat">
                <div className="stat-number">150+</div>
                <div className="stat-label">Countries</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Background Section */}
      <section className="footer-background-section">
        <div className="background-spacer"></div>
      </section>

      {/* Footer Links */}
      <section className="footer-links">
        <div className="container">
          <div className="footer-grid">
            <motion.div
              className="footer-brand"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="brand-logo">
                <img src="/Footer.png" alt="USD.P" className="footer-logo" />
              </div>
              <p className="brand-description">
                The world's most trusted stablecoin, enabling instant, secure, 
                and cost-effective digital transactions for the next generation of finance.
              </p>
              <div className="social-links">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    className="social-link"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.name}
                  >
                    <span className="social-icon">{getSocialIcon(social.icon)}</span>
                  </motion.a>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="footer-section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="footer-section-title">Product</h3>
              <ul className="footer-section-links">
                {footerLinks.product.map((link) => (
                  <li key={link.name}>
                    <a href={link.href} className="footer-link">{link.name}</a>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="footer-section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="footer-section-title">Company</h3>
              <ul className="footer-section-links">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <a href={link.href} className="footer-link">{link.name}</a>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="footer-section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="footer-section-title">Resources</h3>
              <ul className="footer-section-links">
                {footerLinks.resources.map((link) => (
                  <li key={link.name}>
                    <a href={link.href} className="footer-link">{link.name}</a>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="footer-section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3 className="footer-section-title">Legal</h3>
              <ul className="footer-section-links">
                {footerLinks.legal.map((link) => (
                  <li key={link.name}>
                    <a href={link.href} className="footer-link">{link.name}</a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer Bottom */}
      <section className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-content">
            <p className="copyright">
              © 2024 E-Pay Dollar. All rights reserved.
            </p>
            <div className="footer-badges">
              <span className="badge">SOC 2 Compliant</span>
              <span className="badge">GENIUS Act Compliant</span>
              <span className="badge">Audited by Immunefi</span>
            </div>
          </div>
        </div>
      </section>

      <div className="footer-background">
        <div className="footer-glow footer-glow-1"></div>
        <div className="footer-glow footer-glow-2"></div>
      </div>
    </footer>
  );
};

// Helper function to get social icons
const getSocialIcon = (iconName: string) => {
  const icons: { [key: string]: React.ComponentType<React.SVGProps<SVGSVGElement>> } = {
    twitter: TwitterIcon,
    discord: DiscordIcon,
    telegram: TelegramIcon,
    github: GithubIcon
  };
  const IconComponent = icons[iconName];
  return IconComponent ? <IconComponent className="cyber-svg-icon" /> : <span>🔗</span>;
};

export default Footer;